import { useState } from "react";
import { CheckCircle2, Send, Trophy, XCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/Cards";
import { Button } from "@/components/ui/Buttons";
import { ConfirmDeleteDialog } from "@/components/ConfirmDeleteDialog";
import { useUpdateProject } from "@/hooks/useProjects";
import type { ProjectDetail, ProjectStatus } from "@/lib/api";

/**
 * Lifecycle close — Mark Submitted / Won / Lost actions on the Summary
 * page. Drives the project status state machine and feeds the AI learning
 * loop on Won (the latest exported response auto-files as a past_rfp
 * reference).
 *
 * State machine the UI exposes:
 *   draft / active  → Mark Submitted
 *   submitted       → Mark Won  / Mark Lost  / Reopen
 *   won             → Reopen
 *   lost            → Reopen
 */
export default function LifecycleCard({
  project,
  projectId,
}: {
  project: ProjectDetail;
  projectId: string;
}) {
  const update = useUpdateProject();
  const [confirmStatus, setConfirmStatus] = useState<ProjectStatus | null>(null);

  const setStatus = (status: ProjectStatus) => setConfirmStatus(status);

  const applyStatus = async () => {
    if (!confirmStatus) return;
    try {
      await update.mutateAsync({
        id: projectId,
        body: { status: confirmStatus },
      });
      toast.success(messageForStatus(confirmStatus));
      setConfirmStatus(null);
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const isClosed =
    project.status === "won" || project.status === "lost" || project.status === "submitted";

  return (
    <>
      <Card>
        <div className="flex items-start gap-3">
          <div
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
            style={{
              backgroundColor:
                project.status === "won"
                  ? "rgba(34,197,94,0.1)"
                  : project.status === "lost"
                    ? "rgba(239,68,68,0.08)"
                    : project.status === "submitted"
                      ? "rgba(96,165,250,0.1)"
                      : "rgba(255,255,255,0.04)",
            }}
          >
            {project.status === "won" ? (
              <Trophy size={16} style={{ color: "#22c55e" }} />
            ) : project.status === "lost" ? (
              <XCircle size={16} style={{ color: "#ef4444" }} />
            ) : project.status === "submitted" ? (
              <Send size={16} style={{ color: "#60A5FA" }} />
            ) : (
              <CheckCircle2 size={16} style={{ color: "#9CA3AF" }} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="text-[10px] uppercase tracking-wider font-bold"
              style={{ color: "#9CA3AF" }}
            >
              Lifecycle
            </p>
            <h3 className="text-sm font-semibold text-white mt-0.5">
              {labelForStatus(project.status)}
            </h3>
            <p className="text-xs mt-1" style={{ color: "#9CA3AF" }}>
              {descriptionForStatus(project.status)}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {(project.status === "draft" || project.status === "active") && (
              <Button
                variant="primary"
                size="sm"
                icon={<Send size={12} />}
                onClick={() => setStatus("submitted")}
                loading={update.isPending}
              >
                Mark Submitted
              </Button>
            )}
            {project.status === "submitted" && (
              <>
                <Button
                  variant="primary"
                  size="sm"
                  icon={<Trophy size={12} />}
                  onClick={() => setStatus("won")}
                  loading={update.isPending}
                >
                  Mark Won
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={<XCircle size={12} />}
                  onClick={() => setStatus("lost")}
                  loading={update.isPending}
                >
                  Mark Lost
                </Button>
              </>
            )}
            {isClosed && (
              <Button
                variant="ghost"
                size="sm"
                icon={<RotateCcw size={12} />}
                onClick={() => setStatus("active")}
                loading={update.isPending}
              >
                Reopen
              </Button>
            )}
          </div>
        </div>
      </Card>

      <ConfirmDeleteDialog
        open={confirmStatus !== null}
        onOpenChange={(o) => !o && setConfirmStatus(null)}
        title={titleForConfirm(confirmStatus)}
        description={descriptionForConfirm(confirmStatus)}
        confirmLabel={confirmLabelFor(confirmStatus)}
        onConfirm={applyStatus}
        busy={update.isPending}
      />
    </>
  );
}

function labelForStatus(s: ProjectStatus): string {
  switch (s) {
    case "draft":
      return "Drafting";
    case "active":
      return "In progress";
    case "submitted":
      return "Submitted";
    case "won":
      return "Won";
    case "lost":
      return "Lost";
  }
}

function descriptionForStatus(s: ProjectStatus): string {
  switch (s) {
    case "draft":
    case "active":
      return "Click Mark Submitted once you've delivered the response to the client.";
    case "submitted":
      return "Awaiting outcome. When you hear back, mark Won or Lost — won bids feed forward into the agency's AI reference library.";
    case "won":
      return "🎉 Bid won. The exported response is now in the reference library and will inform future drafts.";
    case "lost":
      return "Bid recorded as lost. The exported response is in the library so the AI can learn from it too — note what went wrong on the file's agency notes.";
  }
}

function messageForStatus(s: ProjectStatus): string {
  switch (s) {
    case "submitted":
      return "Marked submitted.";
    case "won":
      return "Bid marked Won — exported response added to your reference library.";
    case "lost":
      return "Bid marked Lost.";
    case "active":
      return "Reopened.";
    default:
      return "Updated.";
  }
}

function titleForConfirm(s: ProjectStatus | null): string {
  switch (s) {
    case "submitted":
      return "Mark this bid as Submitted?";
    case "won":
      return "Mark this bid as Won?";
    case "lost":
      return "Mark this bid as Lost?";
    case "active":
      return "Reopen this bid?";
    default:
      return "Update lifecycle?";
  }
}

function descriptionForConfirm(s: ProjectStatus | null): string {
  switch (s) {
    case "submitted":
      return "Confirms the response has been delivered to the client. The bid moves to Submitted; you can mark Won or Lost when you hear back.";
    case "won":
      return "Records the bid as won and adds the latest exported response to the agency's reference library, where the AI can pull from it for future bids.";
    case "lost":
      return "Records the bid as lost. The latest exported response is filed for reference too — annotate it with what went wrong if known.";
    case "active":
      return "Move the bid back to In progress so you can edit and re-export.";
    default:
      return "";
  }
}

function confirmLabelFor(s: ProjectStatus | null): string {
  switch (s) {
    case "submitted":
      return "Mark Submitted";
    case "won":
      return "Mark Won";
    case "lost":
      return "Mark Lost";
    case "active":
      return "Reopen";
    default:
      return "Confirm";
  }
}
